import React, { useCallback, useMemo } from 'react'
import { makeStyles } from '@material-ui/styles'
import { IconButton, Typography } from '@material-ui/core'
import { ChevronLeft, ChevronRight } from '@material-ui/icons'
import Dropdown from 'core/elements/dropdown'
import Theme from 'core/themes/model'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  info: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  pageSizeControl: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  dropdown: {
    display: 'inline-flex',
    flexFlow: 'row nowrap',
    justifyContent: 'right',
    alignItems: 'center',
    '& .inputFrame': {
      borderWidth: 1,
      marginRight: theme.spacing(1),
    },
    '& .placeholder': {
      fontWeight: 400,
    },
    '& .dropdownMenu': {
      minWidth: 50,
    },
  },
}))

/**
 * Props for the ServerSidePagination component
 */
export interface ServerSidePaginationProps {
  /** Current page number (0-indexed) */
  currentPage: number
  /** Number of records per page */
  pageSize: number
  /** Total count of records (optional - shows "X loaded" if not provided) */
  totalCount?: number
  /** Whether there's a next page available */
  hasNextPage: boolean
  /** Callback when page changes */
  onPageChange: (newPage: number, marker?: string) => void
  /** Callback when page size changes */
  onPageSizeChange: (newPageSize: number) => void
  /** Marker for the next page (for API calls) */
  nextMarker?: string | null
  /** Number of currently loaded records (for display purposes) */
  loadedCount?: number
}

const PAGE_SIZE_OPTIONS = [10, 25, 50].map((value) => ({ value }))

/**
 * Generic server-side pagination component.
 *
 * This component is decoupled from session state and receives all data via props.
 * It displays:
 * - Current record range (e.g., "Showing 1-10 of 100")
 * - Previous/Next page navigation
 * - Page size selector (10, 25, 50)
 *
 * @example
 * ```tsx
 * <ServerSidePagination
 *   currentPage={0}
 *   pageSize={10}
 *   totalCount={100}
 *   hasNextPage={true}
 *   onPageChange={(page, marker) => handlePageChange(page, marker)}
 *   onPageSizeChange={(size) => handlePageSizeChange(size)}
 *   nextMarker="abc123"
 * />
 * ```
 */
export default function ServerSidePagination({
  currentPage,
  pageSize,
  totalCount,
  hasNextPage,
  onPageChange,
  onPageSizeChange,
  nextMarker,
  loadedCount,
}: ServerSidePaginationProps) {
  const classes = useStyles()

  // Use totalCount if available, otherwise fall back to loadedCount
  const displayTotal = totalCount ?? loadedCount ?? 0
  const showLoadedSuffix = totalCount == null && loadedCount != null && loadedCount > 0

  // Calculate display range
  const startRecord = displayTotal > 0 ? currentPage * pageSize + 1 : 0
  const endRecord = Math.min((currentPage + 1) * pageSize, displayTotal)

  const hasPrevPage = useMemo(() => currentPage > 0, [currentPage])

  const handleNext = useCallback(() => {
    if (hasNextPage) {
      onPageChange(currentPage + 1, nextMarker || undefined)
    }
  }, [hasNextPage, currentPage, onPageChange, nextMarker])

  const handlePrev = useCallback(() => {
    if (hasPrevPage) {
      onPageChange(currentPage - 1)
    }
  }, [hasPrevPage, currentPage, onPageChange])

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      onPageSizeChange(newSize)
    },
    [onPageSizeChange],
  )

  return (
    <div className={classes.root}>
      <Typography className={classes.info}>
        {displayTotal > 0
          ? `Showing ${startRecord}-${endRecord} of ${displayTotal}${
              showLoadedSuffix ? ' loaded' : ''
            }`
          : 'No records'}
      </Typography>
      <div className={classes.controls}>
        <IconButton
          onClick={handlePrev}
          disabled={!hasPrevPage}
          size="small"
          aria-label="Previous page"
        >
          <ChevronLeft />
        </IconButton>
        <Typography className={classes.info}>Page {currentPage + 1}</Typography>
        <IconButton
          onClick={handleNext}
          disabled={!hasNextPage}
          size="small"
          aria-label="Next page"
        >
          <ChevronRight />
        </IconButton>
      </div>
      <div className={classes.pageSizeControl}>
        <Dropdown
          className={classes.dropdown}
          label="Results per page"
          compact
          items={PAGE_SIZE_OPTIONS}
          value={pageSize}
          onChange={handlePageSizeChange}
        />
      </div>
    </div>
  )
}
